import { createRouter, createWebHistory, type RouteRecordRaw, type RouteLocationNormalized } from 'vue-router';

// Lazy-load all views — keeps the initial bundle small
const LoginView      = () => import('@/views/auth/LoginView.vue');
const SignupView     = () => import('@/views/auth/SignupView.vue');
const VerifyView     = () => import('@/views/auth/VerifyView.vue');
const SsoDoneView    = () => import('@/views/auth/SsoDoneView.vue');
const DemoView       = () => import('@/views/auth/DemoView.vue');
const AcceptInviteView = () => import('@/views/AcceptInviteView.vue');
const DashboardView  = () => import('@/views/DashboardView.vue');
const BotsView       = () => import('@/views/bots/BotsView.vue');
const BotEditView    = () => import('@/views/bots/BotEditView.vue');
const ConversationsView = () => import('@/views/ConversationsView.vue');
const LeadsView      = () => import('@/views/LeadsView.vue');
const KnowledgeView  = () => import('@/views/KnowledgeView.vue');
const PluginsView    = () => import('@/views/PluginsView.vue');
const StatsView      = () => import('@/views/StatsView.vue');
const AdminStatsView = () => import('@/views/AdminStatsView.vue');
const AdminUsageView = () => import('@/views/AdminUsageView.vue');
const AdminManagedView = () => import('@/views/AdminManagedView.vue');
const AdminFeedbackView = () => import('@/views/AdminFeedbackView.vue');
const AdminUsersView = () => import('@/views/AdminUsersView.vue');
const AdminContactRequestsView = () => import('@/views/AdminContactRequestsView.vue');
const CompaniesView = () => import('@/views/CompaniesView.vue');
const BillingView    = () => import('@/views/BillingView.vue');
const TeamView       = () => import('@/views/TeamView.vue');
const SettingsView   = () => import('@/views/SettingsView.vue');
const AccountView    = () => import('@/views/AccountView.vue');
const NotFoundView   = () => import('@/views/NotFoundView.vue');
const AppShell       = () => import('@/components/layout/AppShell.vue');

/** Old bot-edit tab → new Brain section/sub (see views/bots/BotEditView.vue). */
const LEGACY_TAB: Record<string, [string, string?]> = {
  overview: ['overview'], general: ['settings'], prompt: ['instructions'],
  widget: ['website', 'appearance'], access: ['website', 'access'], translations: ['languages'],
  knowledge: ['knowledge'], embed: ['website', 'embed'], limits: ['settings'], plan: ['plan'],
};
async function legacyBotRedirect(to: RouteLocationNormalized) {
  const [section, sub] = LEGACY_TAB[String(to.params.tab || 'general')] ?? ['overview'];
  try {
    const { botsApi } = await import('@/services/bots');
    const r = await botsApi.get(String(to.params.id));
    return { name: 'brain', params: { pid: r.bot.public_id, section, ...(sub ? { sub } : {}) }, query: to.query };
  } catch {
    return { name: 'bots' };
  }
}

const routes: RouteRecordRaw[] = [
  // Routes use `meta.titleKey` (translation key) so the document title +
  // page title in AppShell stay locale-aware. The legacy `meta.title` field
  // is kept as a fallback for any consumers we haven't migrated yet.
  {
    path: '/login',
    name: 'login',
    component: LoginView,
    meta: { layout: 'blank', titleKey: 'auth.login.title' },
  },
  {
    path: '/signup',
    name: 'signup',
    component: SignupView,
    meta: { layout: 'blank', titleKey: 'auth.signup.title' },
  },
  {
    path: '/verify',
    name: 'verify',
    component: VerifyView,
    meta: { layout: 'blank', titleKey: 'auth.verify.pendingTitle' },
  },
  {
    path: '/sso/done',
    name: 'sso-done',
    component: SsoDoneView,
    meta: { layout: 'blank', titleKey: 'auth.ssoDone.finishing' },
  },
  {
    path: '/demo',
    name: 'demo',
    component: DemoView,
    meta: { layout: 'blank', titleKey: 'auth.demo.loading' },
  },
  {
    path: '/invite/:token',
    name: 'accept-invite',
    component: AcceptInviteView,
    meta: { layout: 'blank', titleKey: 'auth.invite.title' },
  },
  {
    path: '/',
    component: AppShell,
    meta: { requiresAuth: true },
    children: [
      { path: '',              name: 'dashboard',     component: DashboardView,     meta: { titleKey: 'nav.dashboard',     icon: 'overview' } },
      // A Brain is addressed by its PUBLIC id (the embed-snippet id), never the
      // row id — /bots/90 told every customer how many bots the platform has.
      { path: 'brains',        name: 'bots',          component: BotsView,          meta: { titleKey: 'nav.brains',        icon: 'bots' } },
      // Activity scoped to one Brain: the same pages, pre-filtered, so opening
      // them from the Brain menu does not throw you out of the Brain's context.
      { path: 'brains/:pid/conversations', name: 'brain-conversations', component: ConversationsView, meta: { titleKey: 'nav.conversations', icon: 'conversations' } },
      { path: 'brains/:pid/leads',         name: 'brain-leads',         component: LeadsView,         meta: { titleKey: 'nav.leads',         icon: 'leads' } },
      { path: 'brains/:pid/stats',         name: 'brain-stats',         component: StatsView,         meta: { titleKey: 'nav.stats',         icon: 'stats' } },
      { path: 'brains/:pid/:section?/:sub?', name: 'brain', component: BotEditView, meta: { titleKey: 'nav.brains', icon: 'bots' } },
      { path: 'bots',          redirect: { name: 'bots' } },
      // /bots/:id redirects to /bots/:id/general so the URL always shows a tab
      // Legacy /bots/:id/:tab links (older emails, bookmarks, the AppShell
      // pending-plan redirect): resolve to the Brain's public id and new section.
      { path: 'bots/:id/:tab?', name: 'bot-edit', component: BotEditView, beforeEnter: legacyBotRedirect, meta: { titleKey: 'nav.brains', icon: 'bots' } },
      { path: 'conversations', name: 'conversations', component: ConversationsView, meta: { titleKey: 'nav.conversations', icon: 'conversations' } },
      { path: 'leads',         name: 'leads',         component: LeadsView,         meta: { titleKey: 'nav.leads',         icon: 'leads' } },
      // Knowledge now lives inside each Brain; the cross-Brain overview stays
      // reachable by URL for admins and old links, but is no longer in the menu.
      { path: 'knowledge',     name: 'knowledge',     component: KnowledgeView,     meta: { titleKey: 'nav.knowledge',     icon: 'knowledge' } },
      { path: 'plugins',       name: 'plugins',       component: PluginsView,       meta: { titleKey: 'nav.plugins',       icon: 'api' } },
      { path: 'stats',         name: 'stats',         component: StatsView,         meta: { titleKey: 'nav.stats',         icon: 'stats' } },
      { path: 'admin/stats',   name: 'admin-stats',   component: AdminStatsView,    meta: { titleKey: 'nav.adminStats', icon: 'stats', requiresAdmin: true } },
      { path: 'admin/usage',   name: 'admin-usage',   component: AdminUsageView,    meta: { titleKey: 'nav.adminUsage', icon: 'stats', requiresAdmin: true } },
      { path: 'admin/managed', name: 'admin-managed', component: AdminManagedView,  meta: { titleKey: 'nav.adminManaged', icon: 'billing', requiresAdmin: true } },
      { path: 'admin/companies', redirect: { name: 'companies' } },
      { path: 'admin/feedback', name: 'admin-feedback', component: AdminFeedbackView, meta: { titleKey: 'nav.adminFeedback', icon: 'conversations', requiresAdmin: true } },
      { path: 'admin/users', name: 'admin-users', component: AdminUsersView, meta: { titleKey: 'nav.adminUsers', icon: 'team', requiresAdmin: true } },
      { path: 'admin/contact-requests',     name: 'admin-contact-requests',        component: AdminContactRequestsView, meta: { titleKey: 'nav.adminContactRequests', icon: 'leads', requiresAdmin: true } },
      { path: 'admin/contact-requests/:id', name: 'admin-contact-request-detail',  component: AdminContactRequestsView, meta: { titleKey: 'nav.adminContactRequests', icon: 'leads', requiresAdmin: true } },
      // One page for companies (migration 0057). `/org` and `/partner` were two
      // views of the same tree from different heights; the tree renders both.
      { path: 'companies',     name: 'companies',     component: CompaniesView,     meta: { titleKey: 'nav.companies', icon: 'team' } },
      { path: 'billing',       name: 'billing',       component: BillingView,       meta: { titleKey: 'nav.billing',  icon: 'billing' } },
      { path: 'team',          name: 'team',          component: TeamView,          meta: { titleKey: 'nav.team',     icon: 'team' } },
      { path: 'account',       name: 'account',       component: AccountView,       meta: { titleKey: 'nav.account',  icon: 'account' } },
      { path: 'settings',      name: 'settings',      component: SettingsView,      meta: { titleKey: 'nav.settings', icon: 'settings', requiresAdmin: true } },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: NotFoundView,
    meta: { layout: 'blank', titleKey: 'notFound.title' },
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 };
  },
});

/**
 * A tab left open across a deploy asks for a lazy chunk whose content hash no
 * longer exists. vue-router surfaces that as a rejected dynamic import and then
 * simply does not navigate — so the menu appears dead with no error shown to the
 * user, which is exactly how it was reported (25/09/2026).
 *
 * Reload once to pick up the new index.html and its current chunk names. The
 * sessionStorage latch is what stops a genuinely broken deploy from turning into
 * an infinite reload loop: if the very next navigation fails the same way, we
 * let it fail visibly instead of hiding it behind a spinning tab.
 */
const CHUNK_RELOAD_KEY = 'vc:chunk-reload';
router.onError((err, to) => {
  const msg = String((err as Error)?.message ?? err);
  const isStaleChunk =
    /dynamically imported module|Importing a module script failed|MIME type|Loading chunk/i.test(msg);
  if (!isStaleChunk) return;

  if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    return;   // already tried — surface the real failure rather than loop
  }
  try { sessionStorage.setItem(CHUNK_RELOAD_KEY, '1'); } catch { /* private mode */ }
  // Full document load, not router.replace: the point is to fetch a fresh
  // index.html, which a client-side navigation would never do.
  window.location.assign(to?.fullPath ?? window.location.pathname);
});

// A navigation that completes proves the current bundle is intact, so the latch
// must not survive into the next deploy window.
router.afterEach(() => {
  try { sessionStorage.removeItem(CHUNK_RELOAD_KEY); } catch { /* ignore */ }
});

router.beforeEach(async (to, _from, next) => {
  // Document title — locale-aware via meta.titleKey, fallback to legacy meta.title
  const { i18n } = await import('@/locales');
  const titleKey = to.meta?.titleKey as string | undefined;
  const titleStr = titleKey ? (i18n.global.t(titleKey) as string) : (to.meta?.title as string | undefined);
  if (titleStr) document.title = `${titleStr} · VistoChat`;

  // Lazy-import the store to avoid pulling Pinia at module-init time
  const { useAuthStore } = await import('@/stores/auth');
  const auth = useAuthStore();

  // Hydrate user state if we have tokens but no in-memory user yet
  if (!auth.user && localStorage.getItem('vc_access_token')) {
    await auth.fetchMe();
  }

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return next({ name: 'login', query: { redirect: to.fullPath } });
  }
  // Admin-gated routes — non-admins get bumped back to dashboard.
  if (to.meta.requiresAdmin && auth.user?.is_admin !== true) {
    return next({ name: 'dashboard' });
  }
  // Tier-gated routes (migration 0054). A separate check from `requiresAdmin`
  // because the tiers are not a ladder here: `distributor` may open /org while
  // `dev` — which IS an admin — may not, and a reseller opens neither.
  const tiers = to.meta.requiresTier as string[] | undefined;
  if (tiers && !tiers.includes(auth.user?.platform_tier ?? 'none')) {
    return next({ name: 'dashboard' });
  }
  // If logged in and visiting /login or /signup, redirect to dashboard
  if ((to.name === 'login' || to.name === 'signup') && auth.isAuthenticated) {
    return next({ name: 'dashboard' });
  }
  next();
});
